import React, { ChangeEvent } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import type { FormState, Step } from './SignupWizard';

interface SignupWizardStepFieldsProps {
  step: Step;
  form: FormState;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onEinChange: (e: ChangeEvent<HTMLInputElement>) => void;
  showPassword: boolean;
  showConfirmPassword: boolean;
  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;
}

const INPUT_CLASS =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100';

const SignupWizardStepFields: React.FC<SignupWizardStepFieldsProps> = ({
  step,
  form,
  onChange,
  onEinChange,
  showPassword,
  showConfirmPassword,
  onTogglePassword,
  onToggleConfirmPassword,
}) => {
  if (step === 1) {
    const hasMinLength = form.password.length >= 8;
    const hasUpper = /[A-Z]/.test(form.password);
    const hasLower = /[a-z]/.test(form.password);
    const hasNumber = /\d/.test(form.password);
    const hasSpecial = /[!@#$%^&*()[\]{};:'"\\|,.<>/?_-]/.test(form.password);

    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">First name</label>
          <input name="first_name" value={form.first_name} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Last name</label>
          <input name="last_name" value={form.last_name} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Email</label>
          <input name="email" type="email" value={form.email} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Phone</label>
          <input name="phone" value={form.phone} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Password</label>
          <div className="relative">
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={onChange}
              className={`${INPUT_CLASS} pr-9`}
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-300"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Confirm password</label>
          <div className="relative">
            <input
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={onChange}
              className={`${INPUT_CLASS} pr-9`}
            />
            <button
              type="button"
              onClick={onToggleConfirmPassword}
              className="absolute inset-y-0 right-2 flex items-center text-slate-500 hover:text-slate-300"
              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showConfirmPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          <p className="font-semibold mb-1">Password must include:</p>
          <ul className="space-y-1 list-disc list-inside">
            <li className={hasMinLength ? 'text-emerald-400' : ''}>At least 8 characters</li>
            <li className={hasUpper ? 'text-emerald-400' : ''}>At least 1 uppercase letter (A–Z)</li>
            <li className={hasLower ? 'text-emerald-400' : ''}>At least 1 lowercase letter (a–z)</li>
            <li className={hasNumber ? 'text-emerald-400' : ''}>At least 1 number (0–9)</li>
            <li className={hasSpecial ? 'text-emerald-400' : ''}>At least 1 special character (!@#$%^&amp; etc.)</li>
          </ul>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Street address</label>
          <input name="address_line1" value={form.address_line1} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Apartment / Suite (optional)
          </label>
          <input name="address_line2" value={form.address_line2} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">City</label>
          <input name="city" value={form.city} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">State / Province</label>
          <input name="state" value={form.state} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Postal code</label>
          <input name="postal_code" value={form.postal_code} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Country</label>
          <input name="country" value={form.country} onChange={onChange} className={INPUT_CLASS} />
        </div>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Legal business name
          </label>
          <input name="business_name" value={form.business_name} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Position title</label>
          <input name="position_title" value={form.position_title} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Business type</label>
          <input name="business_type" value={form.business_type} onChange={onChange} className={INPUT_CLASS} />
        </div>
        <div className="flex items-center space-x-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            name="use_personal_for_business"
            checked={form.use_personal_for_business}
            onChange={onChange}
            className="h-4 w-4 border-slate-300 dark:border-slate-600"
          />
          <span>Use my personal address as my business address</span>
        </div>
        {!form.use_personal_for_business && (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Business address
              </label>
              <input
                name="business_address_line1"
                value={form.business_address_line1}
                onChange={onChange}
                className={INPUT_CLASS}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Business city</label>
              <input name="business_city" value={form.business_city} onChange={onChange} className={INPUT_CLASS} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Business state</label>
              <input name="business_state" value={form.business_state} onChange={onChange} className={INPUT_CLASS} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Business postal code
              </label>
              <input
                name="business_postal_code"
                value={form.business_postal_code}
                onChange={onChange}
                className={INPUT_CLASS}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">Business country</label>
              <input
                name="business_country"
                value={form.business_country}
                onChange={onChange}
                className={INPUT_CLASS}
              />
            </div>
          </div>
        )}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            EIN Number (9 digits)
          </label>
          <input
            name="ein_number"
            value={form.ein_number}
            onChange={onChange}
            placeholder="XX-XXXXXXX"
            maxLength={10}
            className={`${INPUT_CLASS} font-mono`}
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">Enter your 9-digit EIN (e.g., 12-3456789)</p>
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            EIN IRS letter (upload)
          </label>
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={onEinChange}
            className="block w-full text-sm text-slate-700 dark:text-slate-200"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 text-sm text-slate-700 dark:text-slate-200">
      <label className="flex items-start space-x-2 cursor-pointer hover:opacity-80 transition-opacity">
        <input
          type="checkbox"
          name="consent_transactional"
          checked={form.consent_transactional}
          onChange={onChange}
          className="mt-1 h-4 w-4 border-slate-300 dark:border-slate-600 cursor-pointer flex-shrink-0"
        />
        <span className="text-sm text-slate-700 dark:text-slate-200">
          By checking this box, I consent to receive transactional messages related to my account, orders, or services I
          have requested. These messages may include appointment reminders, order confirmations, and account
          notifications among others. Message frequency may vary. Message &amp; Data rates may apply. Reply HELP for
          help or STOP to opt-out.
        </span>
      </label>
      <label className="flex items-start space-x-2 cursor-pointer hover:opacity-80 transition-opacity">
        <input
          type="checkbox"
          name="consent_marketing"
          checked={form.consent_marketing}
          onChange={onChange}
          className="mt-1 h-4 w-4 border-slate-300 dark:border-slate-600 cursor-pointer flex-shrink-0"
        />
        <span className="text-sm text-slate-700 dark:text-slate-200">
          By checking this box, I consent to receive marketing and promotional messages, including special offers,
          discounts, new product updates among others. Message frequency may vary. Message &amp; Data rates may apply.
          Reply HELP for help or STOP to opt-out.
        </span>
      </label>
      <label
        htmlFor="accepted_terms"
        className="flex items-start space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <input
          id="accepted_terms"
          type="checkbox"
          name="accepted_terms"
          checked={form.accepted_terms}
          onChange={onChange}
          className="mt-1 h-4 w-4 border-slate-300 dark:border-slate-600 cursor-pointer"
        />
        <span className="text-sm text-slate-700 dark:text-slate-200">
          I have read and agree to the{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            Terms and Conditions
          </a>
          {' '}and acknowledge the{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>
    </div>
  );
};

export default SignupWizardStepFields;
